import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { caseStudies } from '../data/mock';

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const study = caseStudies.find(s => s.slug === slug);

  if (!study) {
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 className="display-large" style={{ marginBottom: '24px' }}>Case Study Not Found</h1>
          <Link to="/case-studies" className="btn-primary">
            <ArrowLeft size={20} />
            Back to Case Studies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px', background: 'var(--bg-primary)' }}>
      {/* Hero */}
      <section style={{ padding: '60px 7.6923%', background: 'var(--bg-primary)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <Link to="/case-studies" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: 'var(--text-muted)',
            textDecoration: 'none',
            fontSize: '16px',
            marginBottom: '32px',
            transition: 'color 0.3s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--brand-primary)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
          >
            <ArrowLeft size={20} />
            Back to Case Studies
          </Link>
          
          <div style={{ fontSize: '16px', color: 'var(--brand-primary)', marginBottom: '16px', fontWeight: 500 }}>
            {study.serviceType} • {study.sector}
          </div>
          <h1 className="display-large" style={{ marginBottom: '24px' }}>
            {study.title}
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            {study.clientType}
          </p>
        </div>
      </section>

      {/* Hero Image */}
      <section style={{ padding: '0 7.6923% 60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <img
            src={study.image}
            alt={study.title}
            style={{
              width: '100%',
              height: '500px',
              objectFit: 'cover',
              border: '1px solid var(--border-subtle)'
            }}
          />
        </div>
      </section>

      {/* Metrics */}
      <section style={{ padding: '60px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '40px',
            textAlign: 'center'
          }}>
            {Object.entries(study.metrics).map(([key, value]) => (
              <div key={key}>
                <div className="display-medium" style={{ color: 'var(--brand-primary)', marginBottom: '8px' }}>
                  {value}
                </div>
                <div className="body-small" style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge */}
      <section style={{ padding: '80px 7.6923%' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '24px' }}>
            Challenge
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
            {study.challenge}
          </p>
        </div>
      </section>

      {/* Approach */}
      <section style={{ padding: '80px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '24px' }}>
            Approach
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
            {study.approach}
          </p>
        </div>
      </section>

      {/* Solution */}
      <section style={{ padding: '80px 7.6923%' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '24px' }}>
            Solution
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '40px' }}>
            {study.solution}
          </p>
          
          <h3 className="heading-2" style={{ marginBottom: '20px' }}>
            Results
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {study.results.map((result, idx) => (
              <li key={idx} style={{
                fontSize: '18px',
                color: 'var(--text-secondary)',
                marginBottom: '16px',
                paddingLeft: '32px',
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  color: 'var(--brand-primary)',
                  fontSize: '20px'
                }}>✓</span>
                {result}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Quote */}
      <section style={{ padding: '80px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            fontSize: '32px',
            fontWeight: 500,
            lineHeight: '1.5',
            color: 'var(--text-primary)',
            fontStyle: 'italic',
            marginBottom: '24px'
          }}>
            "{study.quote}"
          </div>
          <div style={{ fontSize: '16px', color: 'var(--text-muted)' }}>
            — Project Stakeholder
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '100px 7.6923%', textAlign: 'center' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="display-large" style={{ marginBottom: '24px' }}>
            Ready for similar results?
          </h2>
          <p className="body-large" style={{ marginBottom: '40px', color: 'var(--text-secondary)' }}>
            Let's discuss how we can help your organization achieve its goals.
          </p>
          <Link to="/contact" className="btn-primary">
            Get started
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetail;
